import React, { useCallback, useRef, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { Components, Logger, Services, Utils, Models, Assets } from "@sendum/core";

import { VisibilityRootState } from "../../store/VisibilityStore";
import { getHistoryPublicInterval } from "../../store/VisibilityActions";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const logger = new Logger("MarkenDownloadBtn");

import classes from "./MarkenDownloadBtn.module.scss";
interface IProps {
	className?: string;
	showUserInfo?: boolean;
}

export const MarkenDownloadBtn = ({ className, showUserInfo }: IProps): JSX.Element => {
	const [isLoading, setLoading] = useState<boolean>(false);
	const chooseFormatModalRef = useRef<Components.IModalRef>(null);

	const device = useSelector((state: VisibilityRootState) => state.Visibility.device);
	const orderId = useSelector((state: VisibilityRootState) => state.Visibility.orderId);
	const shipment = useSelector((state: VisibilityRootState) => state.Visibility.shipment);
	const shipments = useSelector((state: VisibilityRootState) => state.Visibility.shipments);
	const trackingStartTime = useSelector((state: VisibilityRootState) => state.Visibility.trackingStartTime);
	const trackingEndTime = useSelector((state: VisibilityRootState) => state.Visibility.trackingEndTime);

	useEffect(() => {
		Services.EventEmitter.emit("VisibilityShowBtn", { isVisible: true });
	}, []);

	useEffect(() => {
		Services.EventEmitter.emit("VisibilityLockCFR21Report", { isLocked: shipments.length > 0 });
	}, [shipments]);

	const waitHistoryReport = (reportName: string): void => {
		if (reportName) {
			const startTime = Utils.time.current();
			const interval = setInterval(() => {
				Services.API.WidgetsApi.getReport(reportName)
					.then((url: string) => {
						setTimeout(() => setLoading(false), 300);
						setTimeout(() => {
							window.location.href = url;
						}, 100);
						clearInterval(interval);
					})
					.catch((error: Error) => {
						logger.error(error);
						if (Utils.time.current() - startTime > Utils.time.MILLISECONDS_OF_5MIN) {
							toast.error("Repor does not ready for 5 min");
							setLoading(false);
							clearInterval(interval);
						}
					});
			}, 5000);
		}
	};

	const download = useCallback((format: string): void => {
		if (isLoading) {
			return;
		}

		const ids = shipments.length === 3
			? [shipments.find(_ => _.leg === "Placement")?.id ?? "", shipments.find(_ => _.leg === "Live")?.id ?? "", shipments.find(_ => _.leg === "Return")?.id ?? ""]
			: [shipment?.id ?? ""];

		const historyTableConfig = device?.getHistoryTableConfig();
		const sensors: { [key: string]: string } = {};
		if (historyTableConfig) {
			for (const column of historyTableConfig.columns) {
				if (column.accessor === "epoch") {
					sensors["Epoch"] = column.Header;
				} else if (column.accessor === "lat") {
					sensors["REPORT:location:latitude"] = "Lat";
				} else if (column.accessor === "lng") {
					sensors["REPORT:location:longitude"] = "Lng";
				} else {
					sensors[Models.PT300State.parseSensor(column.accessor, false)] = column.Header;
				}
			}
		}

		setLoading(true);
		const data = {
			shipments: ids,
			device: device?.id || "",
			sensors,
			timezone: Utils.time.getTz(),
			format,
			header: `HAWB,${shipment?.masterTrackingNo || "---"},Client Id,${shipment?.name || "---"},Container,${shipment?.cargoes[0]?.containerId || "---"},Device,${shipment?.devices[0]?.displayName || "---"}`,
		};
		if (!showUserInfo) {
			data["start"] = trackingStartTime;
			data["end"] = trackingEndTime || Utils.time.current()
		}
		Services.API.WidgetsApi.generateHistoryReport(data)
			.then(waitHistoryReport)
			.catch((error: Error) => {
				setLoading(false);
				logger.error(error);
				toast.error(error.message);
			});
	}, [showUserInfo, device, shipments, shipment, isLoading, trackingStartTime, trackingEndTime]);

	const downloadByEvent = useCallback((format: string): void => {
		if (format === "CFR21" || format === "CFR21_ClientView") {
			const TRIP_REPORT_TYPE = "21-cfr-part-11-summary-widget-report";
			if (!shipment || !device || !orderId) {
				return;
			}

			const isShared = (/^\/visibility\/(shared)/g).test(window.location.pathname);
			const isPublic = (/^\/visibility\/(public)/g).test(window.location.pathname);
			const { startTime, endTime } = (isShared || isPublic || format === "CFR21_ClientView") ? Utils.getHistoryEndToEndInterval(shipment, orderId, ["Live"]) : { startTime: undefined, endTime: undefined };

			Services.API.WidgetsApi.generateReport(shipment.id, device.id, TRIP_REPORT_TYPE, endTime, startTime)
				.then(reportName => {
					Services.EventEmitter.emit("VisibilityAwaitReport", { reportName });
				})
				.catch((error: Error) => {
					logger.error(error);
					toast.error(error.message);
				});
		} else {
			const ids = shipments.length === 3
				? [shipments.find(_ => _.leg === "Placement")?.id ?? "", shipments.find(_ => _.leg === "Live")?.id ?? "", shipments.find(_ => _.leg === "Return")?.id ?? ""]
				: [shipment?.id ?? ""];

			const historyTableConfig = device?.getHistoryTableConfig();
			const sensors: { [key: string]: string } = {};
			if (historyTableConfig) {
				for (const column of historyTableConfig.columns) {
					if (column.accessor === "epoch") {
						sensors["Epoch"] = column.Header;
					} else if (column.accessor === "lat") {
						sensors["REPORT:location:latitude"] = "Lat";
					} else if (column.accessor === "lng") {
						sensors["REPORT:location:longitude"] = "Lng";
					} else {
						sensors[Models.PT300State.parseSensor(column.accessor, false)] = column.Header;
					}
				}
			}
			if (ids.length === 0 || !device) {
				return;
			}

			const data = {
				shipments: ids,
				device: device?.id,
				sensors,
				timezone: Utils.time.getTz(),
				format: format.toLowerCase(),
				header: `HAWB,${shipment?.masterTrackingNo || "---"},Client Id,${shipment?.name || "---"},Container,${shipment?.cargoes[0]?.containerId || "---"},Device,${shipment?.devices[0]?.displayName || "---"}`,
			};
			if (!showUserInfo) {
				data["start"] = trackingStartTime;
				data["end"] = trackingEndTime || Utils.time.current();
			}
			Services.API.WidgetsApi.generateHistoryReport(data)
				.then(reportName => {
					Services.EventEmitter.emit("VisibilityAwaitReport", { reportName });
				})
				.catch((error: Error) => {
					logger.error(error);
					toast.error(error.message);
				});
		}
	}, [shipment, device, orderId, shipments, showUserInfo, trackingStartTime, trackingEndTime]);

	useEffect(() => {
		const VisibilityDownloadReportHandler = (payload: { type: string }): void => downloadByEvent(payload.type);

		Services.EventEmitter.on("VisibilityDownloadReport", VisibilityDownloadReportHandler);

		return () => {
			Services.EventEmitter.off("VisibilityDownloadReport", VisibilityDownloadReportHandler);
		};
	}, [downloadByEvent]);

	const onClickDownloadCsvBtnHandler = useCallback((event: React.MouseEvent<HTMLImageElement>): void => {
		event.preventDefault();
		download("csv");
		chooseFormatModalRef.current?.close();
	}, [download]);

	const onClickDownloadPdfBtnHandler = useCallback((event: React.MouseEvent<HTMLImageElement>): void => {
		event.preventDefault();
		download("pdf");
		chooseFormatModalRef.current?.close();
	}, [download]);

	const onClickDownloadBtnHandler = useCallback((): void => {
		chooseFormatModalRef.current?.open();
	}, []);

	return (
		<>
			<Components.DeviceInfoButton
				className={className}
				onClick={onClickDownloadBtnHandler}
				type={"marken-download"}
				isLoading={isLoading}
			/>
			<Components.Modal ref={chooseFormatModalRef} >
				<div className={[classes.buttons, classes.buttons__row].join(" ")}>
					<img className={classes.buttons__downloadImg} alt={""} src={Assets.Pdf} onClick={onClickDownloadPdfBtnHandler} />
					<img className={classes.buttons__downloadImg} alt={""} src={Assets.Csv} onClick={onClickDownloadCsvBtnHandler} />
				</div>
			</Components.Modal>
		</>
	);
};
